import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Lock, Mail, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

function formatDetail(detail) {
  if (!detail) return "Errore. Riprova.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

export default function AdminLogin() {
  const { login, user, loading } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();

  if (loading) {
    return (
      <div className="min-h-screen bg-ink grid place-items-center text-white/60">
        <Loader2 className="animate-spin" />
      </div>
    );
  }

  if (user) return <Navigate to="/admin" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await login(email.trim().toLowerCase(), password);
      toast.success("Bentornato.");
      navigate("/admin");
    } catch (err) {
      toast.error(formatDetail(err?.response?.data?.detail) || "Login fallito.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-ink flex items-center justify-center px-6 relative overflow-hidden">
      <div className="absolute inset-0 opacity-30 pointer-events-none">
        <div className="absolute top-[-20%] left-[-10%] w-[500px] h-[500px] rounded-full bg-ember blur-[180px]" />
        <div className="absolute bottom-[-20%] right-[-10%] w-[500px] h-[500px] rounded-full bg-gold/50 blur-[180px]" />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="relative w-full max-w-md"
        data-testid="admin-login-container"
      >
        <div className="flex items-center gap-3 mb-10 justify-center">
          <span className="w-11 h-11 rounded-full grid place-items-center bg-ember text-white font-display font-bold text-xl shadow-[0_0_30px_rgba(200,16,46,0.5)]">龍</span>
          <span className="font-display text-xl tracking-wide">Drago <span className="text-gold">Ramen</span></span>
        </div>

        <div className="rounded-3xl bg-ash/70 backdrop-blur-xl border border-white/10 p-9">
          <div className="flex items-center gap-3 mb-2">
            <span className="h-px w-8 bg-gold" />
            <span className="text-xs uppercase tracking-[0.32em] text-gold font-medium">Area Riservata</span>
          </div>
          <h1 className="font-display text-3xl md:text-4xl font-semibold" data-testid="admin-login-title">
            Accedi alla dashboard.
          </h1>
          <p className="mt-3 text-white/60 text-sm">
            Solo il personale autorizzato può gestire le prenotazioni.
          </p>

          <form onSubmit={submit} className="mt-8 space-y-5" data-testid="admin-login-form">
            <label className="block">
              <span className="block text-xs uppercase tracking-[0.18em] text-white/50 mb-2">Email</span>
              <div className="relative">
                <Mail size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40" />
                <input
                  type="email"
                  required
                  autoComplete="username"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="admin-email-input"
                  className="w-full bg-black/40 border border-white/10 rounded-xl pl-11 pr-4 py-3 text-white placeholder:text-white/35 outline-none focus:border-gold focus:ring-2 focus:ring-gold/20 transition-colors duration-200"
                  placeholder="admin@dragoramen.it"
                />
              </div>
            </label>

            <label className="block">
              <span className="block text-xs uppercase tracking-[0.18em] text-white/50 mb-2">Password</span>
              <div className="relative">
                <Lock size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40" />
                <input
                  type="password"
                  required
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  data-testid="admin-password-input"
                  className="w-full bg-black/40 border border-white/10 rounded-xl pl-11 pr-4 py-3 text-white placeholder:text-white/35 outline-none focus:border-gold focus:ring-2 focus:ring-gold/20 transition-colors duration-200"
                  placeholder="••••••••"
                />
              </div>
            </label>

            <button
              type="submit"
              disabled={submitting}
              data-testid="admin-login-submit"
              className="w-full inline-flex items-center justify-center gap-3 bg-ember hover:bg-emberDeep disabled:opacity-60 disabled:cursor-not-allowed text-white px-8 py-3.5 rounded-full font-medium tracking-wide transition-colors duration-300 shadow-[0_10px_40px_-10px_rgba(200,16,46,0.6)]"
            >
              {submitting ? <><Loader2 size={18} className="animate-spin" /> Accesso...</> : "Entra"}
            </button>
          </form>
        </div>

        <div className="mt-6 text-center">
          <a href="/" className="text-xs text-white/40 hover:text-gold transition-colors" data-testid="admin-back-home">
            ← Torna al sito
          </a>
        </div>
      </motion.div>
    </div>
  );
}
